<template>
  <div class="font-sans bg-[#f6f4f1] text-[#1f2937] min-h-screen flex flex-col">
    <AppHeader />

    <div class="max-w-7xl mx-auto w-full px-4 py-8">
      <div class="text-center mb-8">
        <h1 class="text-3xl font-bold text-[#152a34] mb-2">Encontre Profissionais Perto de Você</h1>
        <p class="text-gray-500 text-sm">Prestadores de serviços presenciais na sua região</p>
      </div>

      <div class="bg-white rounded-2xl shadow p-6 mb-6 border border-[#e0dcd6]">
        <label class="text-sm font-medium text-[#152a34] block mb-1">Sua localização (cidade ou endereço)</label>
        <div class="relative">
          <input
            v-model="locationText"
            @input="onLocationInput"
            @focus="showLocationSuggestions = !!addressSuggestions.length"
            @blur="onLocationBlur"
            type="text"
            placeholder="Ex.: São Paulo, SP ou Rua Augusta, 100"
            class="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52]"
            :class="{ 'border-green-500': userLat != null }"
          />
          <p v-if="locationLoading" class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
            <span class="mdi mdi-loading animate-spin text-lg"></span>
          </p>

          <div
            v-if="addressSuggestions.length && showLocationSuggestions"
            class="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-lg z-20 max-h-56 overflow-auto mt-1"
          >
            <button
              v-for="opt in addressSuggestions"
              :key="opt.place_id || (opt.lat + ',' + opt.lng)"
              type="button"
              @click="selectAddress(opt)"
              class="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm border-b border-gray-100 last:border-0 flex items-start gap-2"
            >
              <span class="mdi mdi-map-marker text-[#b99a52] mt-0.5 shrink-0"></span>
              <span>{{ opt.display_name }}</span>
            </button>
          </div>
        </div>

        <p v-if="locationText && !userLat && !locationLoading" class="text-amber-700 text-xs mt-1">
          Selecione uma opção da lista para habilitar a busca.
        </p>
      </div>

      <div class="bg-white rounded-2xl shadow p-6 mb-6 border border-[#e0dcd6]">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label class="text-sm font-medium text-[#152a34] block mb-1">Categoria (opcional)</label>

            <div class="relative">
              <input
                v-model="categorySearch"
                @input="onCategorySearch"
                type="text"
                placeholder="Buscar categoria..."
                class="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52]"
              />

              <div
                v-if="categoryOptions.length"
                class="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg z-10 max-h-48 overflow-auto mt-1"
              >
                <button
                  v-for="opt in categoryOptions"
                  :key="opt.id"
                  type="button"
                  @click="selectCategory(opt)"
                  class="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm flex items-center gap-3"
                >
                  <div class="w-9 h-9 rounded-xl bg-[#f4efe6] border border-[#eadfca] flex items-center justify-center shrink-0">
                    <img
                      v-if="getCategorySvg(opt)"
                      :src="getCategorySvg(opt)"
                      :alt="opt.description"
                      class="w-5 h-5 object-contain"
                    />
                    <span
                      v-else
                      :class="['mdi', opt.icon_type !== 'svg' && opt.icon ? opt.icon : 'mdi-tag']"
                      class="text-[#b99a52] text-lg"
                    ></span>
                  </div>
                  <span class="text-[#152a34]">{{ opt.description }}</span>
                </button>
              </div>
            </div>

            <span
              v-if="selectedCategory"
              class="inline-flex items-center gap-2 mt-2 px-3 py-1.5 bg-[#f4efe6] border border-[#d6c7a5] text-[#6d5d3a] rounded-full text-xs"
            >
              <img
                v-if="getCategorySvg(selectedCategory)"
                :src="getCategorySvg(selectedCategory)"
                :alt="selectedCategory.description"
                class="w-4 h-4 object-contain"
              />
              <span
                v-else
                :class="['mdi', selectedCategory.icon || 'mdi-tag']"
                class="text-sm"
              ></span>

              {{ selectedCategory.description }}

              <button @click="clearSelectedCategory" class="ml-1 hover:text-red-500">✕</button>
            </span>
          </div>

          <div>
            <label class="text-sm font-medium text-[#152a34] block mb-1">Raio (km)</label>
            <div class="flex items-center gap-3">
              <input
                v-model.number="radiusKm"
                type="range"
                min="1"
                :max="MAX_RADIUS_KM"
                step="1"
                class="w-full accent-[#b99a52]"
              />
              <div class="w-20 shrink-0">
                <input
                  v-model.number="radiusKm"
                  type="number"
                  min="1"
                  :max="MAX_RADIUS_KM"
                  step="1"
                  class="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52]"
                />
              </div>
            </div>
            <p class="text-xs text-gray-400 mt-1">
              Limite também depende do raio definido pelo prestador.
            </p>
          </div>

          <div class="flex items-end">
            <button
              @click="search"
              :disabled="(userLat == null || userLng == null) || searching"
              class="w-full py-2 bg-[#b99a52] text-white rounded-xl font-semibold hover:bg-[#a08840] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <span v-if="searching" class="mdi mdi-loading animate-spin"></span>
              <span v-else class="mdi mdi-magnify"></span>
              {{ searching ? 'Buscando...' : 'Buscar Categorias' }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="searching" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        <div
          v-for="i in 6"
          :key="i"
          class="bg-white rounded-2xl shadow p-5 border border-[#e0dcd6] animate-pulse"
        >
          <div class="flex items-center gap-3 mb-4">
            <div class="w-12 h-12 bg-gray-200 rounded-2xl"></div>
            <div class="flex-1">
              <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div class="h-3 bg-gray-100 rounded w-1/2"></div>
            </div>
          </div>
          <div class="h-9 bg-gray-100 rounded-xl w-full mt-4"></div>
        </div>
      </div>

      <div v-if="searched && !searching">
        <div v-if="categories.length === 0" class="text-center py-16 text-gray-400">
          <span class="mdi mdi-tag-search text-5xl block mb-3"></span>
          <p class="text-lg">Nenhuma categoria encontrada nesta área.</p>
          <p class="text-sm mt-1">Tente outra localização.</p>
        </div>

        <div v-else>
          <p class="text-sm text-gray-500 mb-4">
            {{ categories.length }} categoria(s) com prestadores na sua região
          </p>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <router-link
              v-for="category in categories"
              :key="category.id"
              :to="buildCategoryRoute(category)"
              class="group bg-white rounded-2xl shadow border border-[#e0dcd6] p-5 transition hover:-translate-y-0.5 hover:shadow-md hover:border-[#d6c7a5]"
            >
              <div class="flex items-start gap-4">
                <div class="w-14 h-14 rounded-2xl bg-[#f4efe6] border border-[#eadfca] flex items-center justify-center shrink-0">
                  <img
                    v-if="getCategorySvg(category)"
                    :src="getCategorySvg(category)"
                    :alt="category.description"
                    class="w-7 h-7 object-contain"
                  />
                  <span
                    v-else
                    :class="['mdi', category.icon_type !== 'svg' && category.icon ? category.icon : 'mdi-tag']"
                    class="text-[#b99a52] text-2xl"
                  ></span>
                </div>

                <div class="flex-1 min-w-0">
                  <h3 class="text-base font-semibold text-[#152a34] leading-5 group-hover:text-[#b99a52] transition">
                    {{ category.description }}
                  </h3>

                  <p class="text-sm text-gray-500 mt-1">
                    {{ formatProviderCount(category.provider_count) }}
                  </p>
                </div>
              </div>

              <div class="mt-5 pt-4 border-t border-[#f0ebe4] flex items-center justify-between">
                <span class="text-sm font-medium text-[#6d5d3a]">
                  Ver profissionais
                </span>
                <span class="mdi mdi-arrow-right text-[#b99a52] text-lg"></span>
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { searchCategories } from '@/services/api'
import api from '@/services/api'
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'

const DEFAULT_RADIUS_KM = 20
const MAX_RADIUS_KM = (() => {
  const v = parseInt(process.env.VUE_APP_MAX_RADIUS_KM || '', 10)
  return Number.isFinite(v) && v > 0 ? v : 200
})()
const googleMapsApiKey = () => process.env.VUE_APP_GOOGLE_MAPS_API_KEY || ''

const locationText = ref('')
const userLat = ref(null)
const userLng = ref(null)
const userCity = ref('')
const userState = ref('')
const locationLoading = ref(false)
const addressSuggestions = ref([])
const showLocationSuggestions = ref(false)

const categorySearch = ref('')
const categoryOptions = ref([])
const selectedCategory = ref(null)

const radiusKm = ref(DEFAULT_RADIUS_KM)
radiusKm.value = Math.min(Math.max(1, radiusKm.value), MAX_RADIUS_KM)

const categories = ref([])
const searching = ref(false)
const searched = ref(false)

let categoryDebounce = null
let locationDebounce = null

const svgContext = require.context('@/assets/category-icons', false, /\.svg$/)

onMounted(() => {
  tryCaptureLocationOnLoad()
})

function tryCaptureLocationOnLoad () {
  if (!navigator.geolocation) {
    console.warn('[Location] navigator.geolocation não disponível')
    return
  }

  locationLoading.value = true

  navigator.geolocation.getCurrentPosition(
    (pos) => {
      reverseGeocode(pos.coords.latitude, pos.coords.longitude)
    },
    (err) => {
      locationLoading.value = false
      console.warn('[Location] Geolocalização negada ou falhou:', err.code, err.message)
    },
    { enableHighAccuracy: true, timeout: 8000 }
  )
}

function extractCityState (addressComponents) {
  const comps = addressComponents || []
  const cityComp = comps.find(c =>
    c.types.includes('locality') || c.types.includes('administrative_area_level_2')
  )
  const stateComp = comps.find(c => c.types.includes('administrative_area_level_1'))
  return {
    city: cityComp?.long_name || '',
    state: stateComp?.short_name || '',
  }
}

function reverseGeocode (lat, lng) {
  const key = googleMapsApiKey()

  if (!key) {
    locationLoading.value = false
    return
  }

  const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${key}&language=pt-BR`

  fetch(url)
    .then((r) => r.json())
    .then((data) => {
      if (data.status === 'OK' && data.results && data.results[0]) {
        const r = data.results[0]
        locationText.value = r.formatted_address
        userLat.value = r.geometry.location.lat
        userLng.value = r.geometry.location.lng
        const { city, state } = extractCityState(r.address_components)
        userCity.value = city
        userState.value = state
      } else {
        console.warn('[Location] Geocoding retornou status:', data.status, data.error_message || '')
      }
    })
    .catch((err) => {
      console.error('[Location] Erro no fetch do geocoding:', err)
    })
    .finally(() => {
      locationLoading.value = false
    })
}

function onLocationInput () {
  showLocationSuggestions.value = true

  const query = locationText.value.trim()

  if (!query || query.length < 3) {
    addressSuggestions.value = []
    userLat.value = null
    userLng.value = null
    userCity.value = ''
    userState.value = ''
    return
  }

  const key = googleMapsApiKey()

  if (!key) {
    addressSuggestions.value = []
    return
  }

  clearTimeout(locationDebounce)

  locationDebounce = setTimeout(() => {
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(query)}&key=${key}&language=pt-BR`

    fetch(url)
      .then((r) => r.json())
      .then((data) => {
        if (data.status === 'OK' && Array.isArray(data.results)) {
          addressSuggestions.value = data.results.slice(0, 8).map((r) => {
            const { city, state } = extractCityState(r.address_components)
            return {
              place_id: r.place_id,
              display_name: r.formatted_address,
              lat: r.geometry.location.lat,
              lng: r.geometry.location.lng,
              city,
              state,
            }
          })
        } else {
          addressSuggestions.value = []
        }
      })
      .catch(() => {
        addressSuggestions.value = []
      })
  }, 400)
}

function onLocationBlur () {
  setTimeout(() => {
    showLocationSuggestions.value = false
  }, 200)
}

function selectAddress (opt) {
  locationText.value = opt.display_name
  userLat.value = parseFloat(opt.lat)
  userLng.value = parseFloat(opt.lng)
  userCity.value = opt.city || ''
  userState.value = opt.state || ''
  addressSuggestions.value = []
  showLocationSuggestions.value = false
}

function onCategorySearch () {
  clearTimeout(categoryDebounce)

  if (!categorySearch.value.trim()) {
    categoryOptions.value = []
    return
  }

  categoryDebounce = setTimeout(async () => {
    try {
      const { data } = await api.get('/categories', {
        params: {
          search: categorySearch.value,
          limit: 10,
          type: 'presential'
        }
      })

      categoryOptions.value = data.data || []
    } catch {
      categoryOptions.value = []
    }
  }, 300)
}

function selectCategory (opt) {
  selectedCategory.value = opt
  categorySearch.value = opt.description
  categoryOptions.value = []
}

function clearSelectedCategory () {
  selectedCategory.value = null
  categorySearch.value = ''
}

function slugify (value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/-+/g, '_')
}

const svgCache = new Map()

function getCategorySvg (category) {
  if (category?.icon_type !== 'svg') return null

  const cacheKey = category?.description || category?.icon || ''
  if (svgCache.has(cacheKey)) return svgCache.get(cacheKey)

  try {
    const iconKey = slugify(cacheKey)
    const result = svgContext(`./${iconKey}.svg`)
    svgCache.set(cacheKey, result)
    return result
  } catch {
    svgCache.set(cacheKey, null)
    return null
  }
}

function formatProviderCount (count) {
  const total = Number(count || 0)

  if (total === 1) return '1 profissional disponível'
  return `${total} profissionais disponíveis`
}

function buildCategoryRoute (category) {
  return {
    name: 'providers-by-category',
    params: {
      categoryId: String(category.id)
    },
    query: {
      lat: userLat.value,
      lng: userLng.value,
      radius: radiusKm.value,
      location: locationText.value,
      city: userCity.value || undefined,
      state: userState.value || undefined,
    }
  }
}

async function search () {
  if (userLat.value == null || userLng.value == null) return

  searching.value = true
  searched.value = true

  try {
    const safeRadius = Math.min(Math.max(1, parseInt(radiusKm.value || DEFAULT_RADIUS_KM, 10)), MAX_RADIUS_KM)
    radiusKm.value = safeRadius
    const params = {
      lat: userLat.value,
      lng: userLng.value,
      radius: safeRadius,
    }

    if (userCity.value) params.city = userCity.value
    if (userState.value) params.state = userState.value

    if (selectedCategory.value) {
      params.category_id = selectedCategory.value.id
    }

    const { data } = await searchCategories(params)
    categories.value = data.data || []
  } catch (err) {
    categories.value = []
    console.error('Erro ao buscar categorias:', err)
  } finally {
    searching.value = false
  }
}
</script>